/**
 * Debug logger with zero runtime cost when disabled.
 *
 * Controlled by merchant config flag `debug: true`.
 * When disabled (default), all log/warn functions are no-ops.
 * console.error is always preserved for real errors.
 *
 * When both debug mode AND Sentry are configured (VITE_SENTRY_DSN),
 * every debug.log/warn call is recorded as a Sentry breadcrumb (free,
 * unlimited, never rate-limited). A periodic snapshot event is sent
 * every 10 seconds (only if new logs exist) to carry those breadcrumbs
 * into Sentry's UI so you can read the full debug timeline.
 *
 * Usage:
 *   import { debug } from '../utils/debug'
 *
 *   // In BookingFlow after merchant data loads:
 *   debug.init(merchantData.config?.debug)
 *
 *   // Everywhere else:
 *   debug.log('message', data)
 *   debug.warn('message', data)
 */

import * as Sentry from '@sentry/react'

const noop = () => {}

let _seq = 0
let _newLogCount = 0
let _snapshotTimer = null
const SNAPSHOT_INTERVAL_MS = 10000

/**
 * Format args into a readable string for breadcrumb messages.
 */
function formatArgs(args) {
  return args.map(a =>
    typeof a === 'string' ? a : JSON.stringify(a, null, 0)
  ).join(' ')
}

/**
 * Record a debug log as a Sentry breadcrumb.
 * Breadcrumbs are free (not events), never rate-limited,
 * and automatically attach to the next Sentry event.
 */
function addDebugBreadcrumb(level, args) {
  try {
    const message = typeof args[0] === 'string' ? args[0] : formatArgs(args)
    const data = {}
    if (args.length > 1) {
      args.slice(1).forEach((arg, i) => {
        data[`arg_${i}`] = typeof arg === 'string' ? arg : JSON.stringify(arg, null, 0)
      })
    }
    Sentry.addBreadcrumb({
      category: 'debug',
      message,
      level: level === 'warning' ? 'warning' : 'info',
      data: Object.keys(data).length > 0 ? data : undefined,
    })
    _newLogCount++
    scheduleSnapshot()
  } catch { /* never break the app */ }
}

/**
 * Schedule a snapshot event that carries all accumulated breadcrumbs
 * to Sentry. Only sends if new logs have been recorded since last snapshot.
 */
function scheduleSnapshot() {
  if (_snapshotTimer) return
  _snapshotTimer = setTimeout(sendSnapshot, SNAPSHOT_INTERVAL_MS)
}

function sendSnapshot() {
  _snapshotTimer = null
  if (_newLogCount === 0) return
  const count = _newLogCount
  _newLogCount = 0

  try {
    Sentry.withScope(scope => {
      scope.setLevel('info')
      scope.setTag('source', 'debug-snapshot')
      scope.setTag('log_count', String(count))
      scope.setFingerprint(['debug-snapshot', String(Date.now()), String(++_seq)])
      Sentry.captureMessage(`[debug] ${count} log(s) — check breadcrumbs for details`, 'info')
    })
  } catch { /* ignore */ }
}

const debug = {
  log: noop,
  warn: noop,
  group: noop,
  groupEnd: noop,

  /** Call once when merchant config is available */
  init(enabled) {
    if (enabled) {
      const sentryEnabled = !!import.meta.env.VITE_SENTRY_DSN && Sentry.isInitialized()

      debug.log = (...args) => {
        console.log(...args)
        if (sentryEnabled) addDebugBreadcrumb('info', args)
      }
      debug.warn = (...args) => {
        console.warn(...args)
        if (sentryEnabled) addDebugBreadcrumb('warning', args)
      }
      debug.group = console.group.bind(console)
      debug.groupEnd = console.groupEnd.bind(console)

      // Flush on page hide (tab close, navigate away, etc.)
      if (sentryEnabled) {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            if (_snapshotTimer) clearTimeout(_snapshotTimer)
            sendSnapshot()
          }
        })
      }
    } else {
      debug.log = noop
      debug.warn = noop
      debug.group = noop
      debug.groupEnd = noop
    }
  },

  /** Force-send a snapshot event carrying all breadcrumbs to Sentry */
  flush() {
    if (_snapshotTimer) clearTimeout(_snapshotTimer)
    sendSnapshot()
  }
}

export { debug }
